import {db, uuid, LexoRank} from "../Database.svelte";
import {readable, get} from "svelte/store";
import {confirm} from "../Components/Confirmation.svelte";
import {generateCSV} from '../lib/csv';
import Dashboard from "./dashboardModel";

import QuestionLibrary from "./QuestionLibraryModel";

import { sortBy } from 'lodash-es';
import QuestionTypes from "../lib/QuestionTypes";
import TemplateSlide from "./templateSlideModel";

export function getSortedValues(obj) {
    let result = sortBy(Object.values(obj), (o) => {
        if (o.details) o = o.details;

        return o.rank_ ? o.rank_ : o._id;
    });

    return result;
}

const header_map = {
    "ID": "id",
    "Category Number": "cat_no",
    "Category Name": "cat_name",
    "Subcategory Number": "subcat_no",
    "Subcategory Name": "subcat_name",
    "Question Title": "title",
    "Question": "text",
    "Question Type": "question_type",
    "References": "references",
    "Guidance": "guidance",
    "Control Number": "control_number",
    "Topic Tags": "topic_tags"
}

function invert(obj) {
    var ret = {};
    for(var key in obj){
      ret[obj[key]] = key;
    }
    return ret;
}

export async function exportAssessmentType(assessment_type_id) {
    let assessment_type = (await AssessmentTypeModel.getAssessmentTypeStatic(assessment_type_id)).assessment_type_data;

    let headers = Object.keys(header_map);
    let mapping_values = new Set();
    let rows = [];
    for (let category of Object.values(assessment_type.categories)) {
        for (let subcategory of Object.values(category.subcategories)) {
            for (let control of Object.values(subcategory.controls)) {
                let row = [
                    control.control_id,
                    category.details.control_number,
                    category.details.name,
                    subcategory.details.control_number,
                    subcategory.details.name,
                    control.name,
                    control.text,
                    control.question_type,
                    control.references.join(","),
                    control.guidance,
                    control.control_number,
                    control.topic_tags.join(","),
                    control.recommendation_map
                ]

                for (let map of Object.keys(control.recommendation_map)) {
                    mapping_values.add(map);
                }

                rows.push(row);
            }
        }
    }

    for (let mapping of mapping_values) {
        headers.push(`mapping:${mapping}`);
    }

    for (let row of rows) {
        let map = row.pop();
        for (let mapping of mapping_values) {
            row.push((map[mapping] || []).join(","));
        }
    }

    return generateCSV(headers, rows);
}

export async function importAssessmentType(name, csv_array) {
    let category_id = "";
    let category_number = "";

    let subcategory_number =  "";
    let subcategory_id = "";

    let control_number = 0;

    let headers = csv_array[0];

    let required_fields = ["cat_no", "cat_name", "subcat_no", "subcat_name", "text"];
    let extra_fields = [];

    let mapping_fields = [];

    let import_map = {}
    for (let i = 0; i < headers.length; i++) {
        let mapped_field = header_map[headers[i]];

        if (required_fields.includes(mapped_field)) {
            required_fields.splice(required_fields.indexOf(mapped_field), 1)
        }

        if (mapped_field) {
            import_map[i] = mapped_field;
        } else if (headers[i].startsWith("mapping:")) {
            import_map[i] = headers[i];
        } else {
            extra_fields.push(headers[i]);
        }
    }

    if (required_fields.length) {
        let inverted_map = invert(header_map)
        confirm(`Missing Required Fields: ${required_fields.map(f => inverted_map[f]).join(", ")}`, true);
    }

    if (extra_fields.length) {
        try {
            await confirm(`The following extra columns will not be imported: ${extra_fields.join(", ")}. Continue?`)
        } catch (e) {
            return;
        }
    }

    let imported_assessment_type = new AssessmentTypeModel((await AssessmentTypeModel.createAssessmentType({name})).id);
    let timestamp = Date.now();

    for (let i = 1; i < csv_array.length; i++) {
        let row = csv_array[i];

        let cat_no, cat_name, subcat_no, subcat_name;


        let control_data = {
            recommendation_map: {}
        }
        for (let j = 0; j < row.length; j++) {
            let field = import_map[j];
            if (!field) continue;

            switch (field) {
                case "cat_no":
                    cat_no = row[j];
                    break;
                case "cat_name":
                    cat_name = row[j];
                    break;
                case "subcat_no":
                    subcat_no = row[j];
                    break;
                case "subcat_name":
                    subcat_name = row[j];
                    break;
                case "references":
                    control_data.references = row[j].split(',').map(s => s.trim()).filter(s => s);
                    break;
                case "topic_tags":
                    control_data.topic_tags = row[j].split(',').map(s => s.trim()).filter(s => s);
                    break
                default:
                    if (field.startsWith("mapping:")) {
                        var [_, answer_value] = field.split(":");
                        control_data.recommendation_map[answer_value] = row[j].split(',').map(s => s.trim()).filter(s => s);
                    } else {
                        control_data[field] = row[j];
                    }
            }
        }

        if (control_data.id && false) {
            console.log(control_data.id)
            return;
        } else {
            // skip first and last lines
            if (!cat_no || cat_no == "Category Number") {
                return
            }

            // check for new category
            if (category_number != cat_no) {
                category_id = imported_assessment_type.addCategory({
                    name: cat_name,
                    control_number: cat_no
                }, timestamp++);

                category_number = cat_no;
                subcategory_number = ""
            }

            // check for new subcategory
            if (subcategory_number != subcat_no) {
                subcategory_id = imported_assessment_type.addSubcategory(category_id, {
                    name: subcat_name,
                    control_number: subcat_no
                }, timestamp++);

                subcategory_number = subcat_no;
            }

            // add control
            imported_assessment_type.addControl(category_id, subcategory_id, {
                id: control_data.id,
                name: control_data.title || "",
                text: control_data.text,
                question_type: control_data.question_type,
                references: control_data.references || [],
                guidance: control_data.guidance || "",
                control_number: control_data.control_number || `${category_number}.${("000" + ++control_number).slice(-3)}`,
                topic_tags: control_data.topic_tags || [],
                recommendation_map: control_data.recommendation_map || {},
            }, timestamp++);
        }
    }
}

export default class AssessmentTypeModel {
    constructor (assessment_type_id) {
        if (!assessment_type_id) return;

        this.assessment_type_id = assessment_type_id;
        this.store = this.getAssessmentTypeStore();

        this.category_rank = LexoRank.middle();
        this.subcategory_ranks = {};
        this.control_ranks = {};

        this.section_rank = LexoRank.middle();
        this.slide_rank = LexoRank.middle();

        this.assessment_type_data = {
            model: this,
            categories: {},
            details: {},
            topic_tags: {},
            policies: [],
            question_tags: [],
            comment_tags: [],
            threats: [],
            threat_matrix_mapping: {},
            report_sections: [],
            dashboard_mappings: {},
            report_slides: [],
            library_control_answer_mappings: {},
            library_control_tag_mappings: {},
            disabled_mappings: [],
            excel_template: {
                _id: `${this.assessment_type_id}:excel_template`,
                template: [{
                    name: "Sheet 1"
                }]
            }
        }
    }

    getAssessmentTypeStore() {
        return readable(undefined, (set) => {
            db.allDocs({
                include_docs: true,
                startkey: this.assessment_type_id,
                endkey: `${this.assessment_type_id}\ufff0`
            }).then((result) => {
                result.rows.forEach((row) => {
                    this.handleDoc(row);
                });

                set(this.assessment_type_data)

                // add slide ranks
                for (let slide of this.assessment_type_data.report_slides) {
                    if (!slide.rank_) {
                        slide.rank_ = this.slide_rank.toString();
                        this.slide_rank = this.slide_rank.genNext();

                        db.put(slide);
                    }
                }
            });

            let changeMonitor = db.changes({
                since: "now",
                live: true,
                include_docs: true,
                filter: "filters/starts_with",
                query_params: {
                    id: this.assessment_type_id
                }
            }).on("change", (change) => {
                this.handleDoc(change);
                set(this.assessment_type_data);
            })

            return () => {
                changeMonitor.cancel();
            }
        })
    }

    static async getAssessmentTypeStatic(assessment_type_id) {
        if (!assessment_type_id) throw new Error("Assessment Type ID is required");

        let handleDoc = (new AssessmentTypeModel()).handleDoc

        let data = {
            assessment_type_id,
            assessment_type_data: {
                categories: {},
                details: {},
                topic_tags: {},
                policies: [],
                question_tags: [],
                comment_tags: [],
                threats: [],
                threat_matrix_mapping: {},
                report_sections: [],
                dashboard_mappings: {},
                report_slides: [],
                library_control_answer_mappings: {},
                library_control_tag_mappings: {},
                disabled_mappings: []
            }
        }

        await  db.allDocs({
            include_docs: true,
            startkey: assessment_type_id,
            endkey: `${assessment_type_id}\ufff0`
        }).then((result) => {
            result.rows.forEach((row) => {
                handleDoc.apply(data, [row]);
            });
        });

        return data;
    }

    handleDoc(row) {
        let id, type, rest;
        let category_id, subcategory_id, control_id;
        let section_id, subsection_id;

        if (row.id == this.assessment_type_id) {
            this.assessment_type_data.details = row.doc;
            return;
        }

        [id, type, ...rest] = row.id.split(":");

        // the numbers are to ensure that the values are processed in order.
        switch (type) {
            case "category":
                // assessment_type_id:category_id
                category_id = rest[0];
                if (!this.assessment_type_data.categories[category_id]) {
                    this.assessment_type_data.categories[category_id] = {
                        subcategories: {}
                    }
                }

                if (row.deleted) {
                    delete this.assessment_type_data.categories[category_id];
                } else {
                    this.assessment_type_data.categories[category_id].details = row.doc
                }

                if (row.doc.rank_ && this.category_rank && row.doc.rank_ > this.category_rank.toString()) {
                    this.category_rank = LexoRank.parse(row.doc.rank_).genNext();
                }

                break;

            case "subcategory":
                // assessment_type_id:category_id:subcategory_id
                [category_id, subcategory_id] = rest;

                // check for category
                if (!this.assessment_type_data.categories[category_id]) {
                    this.assessment_type_data.categories[category_id] = {
                        subcategories: {}
                    }
                }

                // check for subcategory
                if (!this.assessment_type_data.categories[category_id].subcategories[subcategory_id]) {
                    this.assessment_type_data.categories[category_id].subcategories[subcategory_id] = {
                        controls: {}
                    }
                }

                if (row.deleted) {
                    delete this.assessment_type_data.categories[category_id].subcategories[subcategory_id];
                } else {
                    this.assessment_type_data.categories[category_id].subcategories[subcategory_id].details = row.doc
                }

                if (row.doc.rank_ && this.subcategory_ranks) {
                    if (!this.subcategory_ranks[category_id] || this.subcategory_ranks[category_id].toString() <= row.doc.rank_) {
                        this.subcategory_ranks[category_id] = LexoRank.parse(row.doc.rank_).genNext();
                    }

                }

                break;

            case "control":
                // assessment_type_id:category_id:subcategory_id:control_id
                [category_id, subcategory_id, control_id] = rest;

                // check for category
                if (!this.assessment_type_data.categories[category_id]) {
                    this.assessment_type_data.categories[category_id] = {
                        subcategories: {}
                    }
                }

                // check for subcategory
                if (!this.assessment_type_data.categories[category_id].subcategories[subcategory_id]) {
                    this.assessment_type_data.categories[category_id].subcategories[subcategory_id] = {
                        controls: {}
                    }
                }

                // remove topic tags for this control
                let oldControl = this.assessment_type_data.categories[category_id].subcategories[subcategory_id].controls[control_id];
                if (oldControl && oldControl.topic_tags){
                    oldControl.topic_tags.forEach(tag => {
                        this.assessment_type_data.topic_tags[tag] -= 1;
                        if (this.assessment_type_data.topic_tags[tag] == 0) {
                            delete this.assessment_type_data.topic_tags[tag];
                        }
                    });
                }

                if (row.deleted) {
                    delete this.assessment_type_data.categories[category_id].subcategories[subcategory_id].controls[control_id];
                } else {

                    // add topic tags for new control
                    if (row.doc.topic_tags) {
                        row.doc.topic_tags.forEach(tag => {
                            this.assessment_type_data.topic_tags[tag] = (this.assessment_type_data.topic_tags[tag] || 0) + 1;
                        })
                    }

                    this.assessment_type_data.categories[category_id].subcategories[subcategory_id].controls[control_id] = row.doc;
                }

                if (row.doc.rank_ && this.control_ranks) {
                    if (!this.control_ranks[subcategory_id] || this.control_ranks[subcategory_id].toString() < row.doc.rank_) {
                        this.control_ranks[subcategory_id] = LexoRank.parse(row.doc.rank_).genNext();
                    }
                }

                break;

            case "library_control_answer_map":
                [control_id, ...rest] = rest;

                if (!this.assessment_type_data.library_control_answer_mappings[control_id]) {
                    this.assessment_type_data.library_control_answer_mappings[control_id] = []
                }

                if (row.deleted) {
                    this.assessment_type_data.library_control_answer_mappings[control_id] = this.assessment_type_data.library_control_answer_mappings[control_id].filter(item => item._id != row.id);
                } else {
                    let found = false;
                    this.assessment_type_data.library_control_answer_mappings[control_id] = this.assessment_type_data.library_control_answer_mappings[control_id].map((item) => {
                        if (item._id == row.id) {
                            found = true;
                            return row.doc;
                        } else {
                            return item;
                        }
                    })

                    if (!found) {
                        this.assessment_type_data.library_control_answer_mappings[control_id].push(row.doc);
                    }
                }

                break;

            case "library_control_tag_map":
                [control_id, ...rest] = rest;

                if (!this.assessment_type_data.library_control_tag_mappings[control_id]) {
                    this.assessment_type_data.library_control_tag_mappings[control_id] = []
                }

                if (row.deleted) {
                    this.assessment_type_data.library_control_tag_mappings[control_id] = this.assessment_type_data.library_control_tag_mappings[control_id].filter(item => item._id != row.id);
                } else {
                    let found = false;
                    this.assessment_type_data.library_control_tag_mappings[control_id] = this.assessment_type_data.library_control_tag_mappings[control_id].map((item) => {
                        if (item._id == row.id) {
                            found = true;
                            return row.doc;
                        } else {
                            return item;
                        }
                    })

                    if (!found) {
                        this.assessment_type_data.library_control_tag_mappings[control_id].push(row.doc);
                    }
                }

                break;

            case "disabled_mapping":
                if (row.deleted) {
                    this.assessment_type_data.disabled_mappings = this.assessment_type_data.disabled_mappings.filter(item => item._id != row.id);
                } else {
                    let found = false;

                    this.assessment_type_data.disabled_mappings = this.assessment_type_data.disabled_mappings.map((item) => {
                        if (item._id == row.id) {
                            found = true;
                            return row.doc;
                        } else {
                            return item
                        }
                    });

                    if (!found) {
                        this.assessment_type_data.disabled_mappings.push(row.doc);
                    }
                }
                break;

            case "policy":
                if (row.deleted) {
                    this.assessment_type_data.policies = this.assessment_type_data.policies.filter(item => item._id != row.id);
                } else {
                    let found = false;

                    this.assessment_type_data.policies = this.assessment_type_data.policies.map((item) => {
                        if (item._id == row.id) {
                            found = true;
                            return row.doc;
                        } else {
                            return item
                        }
                    });

                    if (!found) {
                        this.assessment_type_data.policies.push(row.doc);
                    }
                }
                break;
            case "tag":
                if (row.deleted) {
                    this.assessment_type_data.question_tags = this.assessment_type_data.question_tags.filter(item => item._id != row.id);
                } else {
                    let found = false;

                    this.assessment_type_data.question_tags = this.assessment_type_data.question_tags.map((item) => {
                        if (item._id == row.id) {
                            found = true;
                            return row.doc;
                        } else {
                            return item
                        }
                    });

                    if (!found) {
                        this.assessment_type_data.question_tags.push(row.doc);
                    }
                }
                break;
            case "comment_tag":
                if (row.deleted) {
                    this.assessment_type_data.comment_tags = this.assessment_type_data.comment_tags.filter(item => item._id != row.id);
                } else {
                    let found = false;

                    this.assessment_type_data.comment_tags = this.assessment_type_data.comment_tags.map((item) => {
                        if (item._id == row.id) {
                            found = true;
                            return row.doc;
                        } else {
                            return item
                        }
                    });

                    if (!found) {
                        this.assessment_type_data.comment_tags.push(row.doc);
                    }
                }
                break;

            case "threat":
                if (row.deleted) {
                    this.assessment_type_data.threats = this.assessment_type_data.threats.filter(item => item._id != row.id);
                } else {
                    let found = false;

                    this.assessment_type_data.threats = this.assessment_type_data.threats.map((item) => {
                        if (item._id == row.id) {
                            found = true;
                            return row.doc;
                        } else {
                            return item
                        }
                    });

                    if (!found) {
                        this.assessment_type_data.threats.push(row.doc);
                    }
                }
                break;
            case "threatMap":
                let [threat_id, threat_control, map_id] = rest;
                if (row.deleted) {
                    this.assessment_type_data.threat_matrix_mapping[threat_control] = this.assessment_type_data.threat_matrix_mapping[threat_control].filter(ctrl => {
                        return ctrl._id != row.id
                    });
                } else {
                    if (this.assessment_type_data.threat_matrix_mapping[threat_control] == undefined) {
                        this.assessment_type_data.threat_matrix_mapping[threat_control] = [row.doc];
                    } else {
                        this.assessment_type_data.threat_matrix_mapping[threat_control].push(row.doc);
                    }
                }
                break
            case "dashboardMapping":
                let [dashboard_id, dashboard_item, mapping_id] = rest;

                if (row.deleted) {
                    this.assessment_type_data.dashboard_mappings[dashboard_id][dashboard_item] = this.assessment_type_data.dashboard_mappings[dashboard_id][dashboard_item].filter(item => {
                        return item._id != row.id
                    });
                } else {
                    if (this.assessment_type_data.dashboard_mappings[dashboard_id] == undefined) {
                        this.assessment_type_data.dashboard_mappings[dashboard_id] = {}
                    }

                    if (this.assessment_type_data.dashboard_mappings[dashboard_id][dashboard_item] == undefined) {
                        this.assessment_type_data.dashboard_mappings[dashboard_id][dashboard_item] = [row.doc]
                    } else {
                        this.assessment_type_data.dashboard_mappings[dashboard_id][dashboard_item].push(row.doc)
                    }
                }
                break
            case "report_section":
                if (row.deleted) {
                    this.assessment_type_data.report_sections = this.assessment_type_data.report_sections.filter(item => item._id != row.id);
                } else {
                    let found = false;

                    this.assessment_type_data.report_sections = this.assessment_type_data.report_sections.map((item) => {
                        if (item._id == row.id) {
                            found = true;
                            return row.doc;
                        } else {
                            return item
                        }
                    });

                    if (!found) {
                        this.assessment_type_data.report_sections.push(row.doc);
                    }

                    if (row.doc.rank_ && this.section_rank && row.doc.rank_ > this.section_rank.toString()) {
                        this.section_rank = LexoRank.parse(row.doc.rank_).genNext();
                    }

                    this.assessment_type_data.report_sections = getSortedValues(this.assessment_type_data.report_sections);
                }
                break;
            case "report_slide":
                if (row.deleted) {
                    this.assessment_type_data.report_slides = this.assessment_type_data.report_slides.filter(item => item._id != row.id);
                } else {
                    let found = false;

                    this.assessment_type_data.report_slides = this.assessment_type_data.report_slides.map((item) => {
                        if (item._id == row.id) {
                            found = true;
                            return row.doc;
                        } else {
                            return item
                        }
                    });

                    if (!found) {
                        this.assessment_type_data.report_slides.push(row.doc);
                    }

                    if (row.doc.rank_ && this.slide_rank && row.doc.rank_ > this.slide_rank.toString()) {
                        this.slide_rank = LexoRank.parse(row.doc.rank_).genNext();
                    }

                    this.assessment_type_data.report_slides = getSortedValues(this.assessment_type_data.report_slides);
                }
                break;
            case "excel_template":
                this.assessment_type_data.excel_template = row.doc
                break;
            default:
                console.warn("UNHANDLED DOCUMENT: ", row);
        }
    }

    static getAssessmentTypeList() {
        let assessment_type_list = [];

        return readable(undefined, (set) => {
            db.find({
                selector: {
                    type: "assessment_type"
                }
            }).then((results) => {
                assessment_type_list = results.docs;
                set(assessment_type_list);
            });

            let changeMonitor = db.changes({
                since: "now",
                live: true,
                include_docs: true,
                filter: 'filters/doc_type',
                query_params: {
                    type: 'assessment_type'
                }
            }).on('change', (change) => {
                if (change.deleted) {
                    assessment_type_list = assessment_type_list.filter((at) => at._id != change.id)
                } else {
                    let found = false;
                    assessment_type_list = assessment_type_list.map((at) => {
                        if (at._id != change.id) return at;
                        found = true;
                        return change.doc
                    })

                    if (!found) assessment_type_list.push(change.doc);
                }

                set(assessment_type_list);
            })

            return () => changeMonitor.cancel();
        })
    }

    static createAssessmentType(assessment_type) {
        let new_assessment_type = {
            _id: uuid(),
            type: "assessment_type",
            name: assessment_type.name
        }

        return db.put(new_assessment_type);
    }

    static async cloneAssessmentType(assessment_type, new_assessment_type_name) {
        let results = await db.allDocs({
            include_docs: true,
            startkey: assessment_type._id,
            endkey: `${assessment_type._id}:\ufff0`
        });

        const new_assessment_type_id = uuid();

        let docs_to_add = [];
        for (let row of results.rows) {
            let doc = row.doc;


            if (doc.type == "assessment_type") {
                doc.name = new_assessment_type_name;
                doc._id = new_assessment_type_id;
            } else {
                let [_, ...rest] = doc._id.split(":");
                let new_doc_id = `${new_assessment_type_id}:${rest.join(":")}`;

                doc._id = new_doc_id;
            }

            delete doc._rev;

            docs_to_add.push(doc);
        }

        return db.bulkDocs(docs_to_add)
    }

    static deleteAssessmentType(assessment_type) {
        db.allDocs({
            include_docs: true,
            startkey: assessment_type._id,
            endkey: `${assessment_type._id}:\ufff0`
        }).then(result => {
            let docs_to_delete = [];
            for (let row of result.rows) {
                row.doc._deleted = true;
                docs_to_delete.push(row.doc);
            }
            db.bulkDocs(docs_to_delete);
        })
    }

    static getLibraryControlMappings(assessment_type, library_control) {
        let recommendation_map = {};

        // get disabled mappings
        let disabled_mappings = new Set();

        for (let disabled_mapping of assessment_type.disabled_mappings) {
            disabled_mappings.add(`${disabled_mapping.map.section_id}:${disabled_mapping.map.map_id}`);
        }

        // get answer mappings
        if (library_control.details.question_type != 'free_text') {
            for (let answer of Object.values(QuestionTypes[library_control.details.question_type].buttons)) {
                recommendation_map[answer.value] = [];
            };

            // library mappings
            for (let mapping of [...library_control.answer_mappings]) {
                for (let section of assessment_type.report_sections) {
                    if (section.section_type == "library" && section.library_type == mapping.details.map.library_id && !section.exclude_mappings) {
                        if (disabled_mappings.has(`${section.section_id}:${mapping.id}`)) continue

                        if (!recommendation_map[mapping.details.answer]) {
                            console.log(library_control, mapping);
                            continue;
                        }

                        recommendation_map[mapping.details.answer].push({
                            type: "library_item",
                            section_id: section.section_id,
                            id: mapping.details.map.id
                        })
                    }
                }
            }

            // assessment_type mappings
            if (assessment_type.library_control_answer_mappings[library_control.details.child_id]) {
                for (let answer_map of assessment_type.library_control_answer_mappings[library_control.details.child_id]) {
                    recommendation_map[answer_map.answer].push(answer_map.map);
                }
            }
        }

        // get tag mappings
        let tag_mapping = {};

        // library mappings
        for (let mapping of [...library_control.tag_mappings]) {
            for (let section of assessment_type.report_sections) {
                if (section.section_type == "library" && section.library_type == mapping.details.map.library_id && !section.exclude_mappings) {
                    if (disabled_mappings.has(`${section.section_id}:${mapping.id}`)) continue

                    let tag_map = {
                        type: "library_item",
                        section_id: section.section_id,
                        id: mapping.details.map.id
                    }

                    if (!tag_mapping[mapping.details.tag]) {
                        tag_mapping[mapping.details.tag] = [tag_map]
                    } else {
                        tag_mapping[mapping.details.tag].push(tag_map);
                    }
                }
            }
        }

        // assessment_type_mappings
        if (assessment_type.library_control_tag_mappings[library_control.details.child_id]) {
            for (let tag_map of assessment_type.library_control_tag_mappings[library_control.details.child_id]) {
                if (!tag_mapping[tag_map.tag_id]) {
                    tag_mapping[tag_map.tag_id] = [tag_map.map];
                } else {
                    tag_mapping[tag_map.tag_id].push(tag_map.map);
                }
            }
        }

        return {
            recommendation_map,
            tag_mapping
        }
    }

    static async flattenAssessmentType(assessment_type) {
        //capture dashboards
        let dashboards = {};
        for (let section of assessment_type.report_sections) {
            if (section.section_type == "dashboard") {
                let dash = await Dashboard.getDashboardStatic(section.dashboard_type)
                dashboards[section.section_name] = {
                    data: dash.dashboard_data,
                    mapping: assessment_type.dashboard_mappings[dash.dashboard_id]
                }
            }
        }

        // calculate question tags list
        let topic_tags = new Set();

        return {
            name: assessment_type.details.name,
            assessment_type_category: assessment_type.details.assessment_type_category,
            policies: assessment_type.policies,
            question_tags: assessment_type.question_tags,
            comment_tags: assessment_type.comment_tags,
            categories: await Promise.all(getSortedValues(assessment_type.categories).map(async (category) => {
                if (category.details.library_id) {
                    let library_category = await QuestionLibrary.getStatic(category.details.library_id);
                    return {
                        id: library_category.details.child_id,
                        name: library_category.details.name,
                        control_number: library_category.details.control_number,
                        subcategories: [...library_category.subcategories].map((library_subcategory) => {
                            return {
                                id: library_subcategory.details.child_id,
                                name: library_subcategory.details.name,
                                control_number: library_subcategory.details.control_number,
                                controls: [...library_subcategory.controls].map((library_control) => {
                                    if (category.details.include_controls && !category.details.include_controls.includes(library_control.details.child_id)) {
                                        return null;
                                    }
                                    if (library_control.details.topic_tags) {
                                        library_control.details.topic_tags.forEach(tag => {
                                            topic_tags.add(tag);
                                        });
                                    }

                                    // get control mappings
                                    let mappings = this.getLibraryControlMappings(assessment_type, library_control);

                                    return {
                                        id: library_control.details.child_id,
                                        name: library_control.details.name,
                                        control_number: library_control.details.control_number,
                                        level: library_control.details.level,
                                        question_type: library_control.details.question_type,
                                        references: library_control.details.references,
                                        text: library_control.details.text,
                                        guidance: library_control.details.guidance,
                                        topic_tags: library_control.details.topic_tags || [],
                                        recommendation_map: mappings.recommendation_map,
                                        tag_mapping: mappings.tag_mapping
                                    }
                                }).filter((c) => c) // remove unincluded_controls
                            }
                        }).filter(sc => sc.controls.length) // remove subcategories with no controls
                    }
                } else {
                    return {
                        id: category.details.category_id,
                        name: category.details.name,
                        control_number: category.details.control_number,
                        subcategories: await Promise.all(getSortedValues(category.subcategories).map(async (subcategory) => {
                            if (subcategory.details.library_id) {
                                let library_subcategory = await QuestionLibrary.getStatic(subcategory.details.library_id);

                                return {
                                    id: library_subcategory.details.child_id,
                                    name: library_subcategory.details.name,
                                    control_number: library_subcategory.details.control_number,
                                    controls: [...library_subcategory.controls].map((library_control) => {
                                        if (library_control.details.topic_tags) {
                                            library_control.details.topic_tags.forEach(tag => {
                                                topic_tags.add(tag);
                                            });
                                        } else {
                                            library_control.details.topic_tags = [];
                                        }

                                        // get control mappings
                                        let mappings = this.getLibraryControlMappings(assessment_type, library_control);

                                        return {
                                            id: library_control.details.child_id,
                                            name: library_control.details.name,
                                            control_number: library_control.details.control_number,
                                            level: library_control.details.level,
                                            question_type: library_control.details.question_type,
                                            references: library_control.details.references,
                                            text: library_control.details.text,
                                            guidance: library_control.details.guidance,
                                            topic_tags: library_control.details.topic_tags || [],
                                            recommendation_map: mappings.recommendation_map,
                                            tag_mapping: mappings.tag_mapping
                                        }
                                    })
                                }
                            } else {
                                return {
                                    id: subcategory.details.subcategory_id,
                                    name: subcategory.details.name,
                                    control_number: subcategory.details.control_number,
                                    controls: getSortedValues(subcategory.controls).map((control) => {
                                        if (control.library_id) {
                                            control = library_controls[control.library_id];
                                        }

                                        control.topic_tags?.forEach((tag => {
                                            topic_tags.add(tag);
                                        }))

                                        return {
                                            id: control.control_id,
                                            name: control.name,
                                            control_number: control.control_number,
                                            level: control.level,
                                            question_type: control.question_type,
                                            references: control.references,
                                            text: control.text,
                                            guidance: control.guidance,
                                            topic_tags: control.topic_tags || [],
                                            recommendation_map: control.recommendation_map || {},
                                            tag_mapping: control.tag_mapping || {}
                                        }
                                    })
                                }
                            }
                        }))
                    }
                }
            })),
            dashboards,
            report_sections: getSortedValues(assessment_type.report_sections),
            topic_tags: [...topic_tags],
            report_slides: await Promise.all(getSortedValues(assessment_type.report_slides).map(async (slide) => {
                if (slide.slide_type == "template_slide") {
                    let template_slide = await TemplateSlide.getStatic(slide.template_id);

                    let vars = template_slide.details.vars;
                    let variables = template_slide.details.default_defs;

                    if (slide.variables) {
                        Object.assign(variables, slide.variables);
                    }

                    let assigns = '';
                    for (let variable of vars) {
                        assigns += `{%- assign ${variable} = ${variables[variable]} %}`;
                    }

                    slide.body = assigns + template_slide.details.body;
                    slide.css = assigns + template_slide.details.css;
                }

                return slide;
            })),
            global_css: assessment_type.details.global_css,
            excel_template: assessment_type.excel_template
        }
    }

    addCategory(category, timestamp) {
        let category_id = uuid(timestamp);
        let new_category = {
            _id: `${this.assessment_type_id}:category:${category_id}`,
            category_id: category_id,
            name: category.name,
            control_number: category.control_number,
            type: "category",
            rank_: this.category_rank.toString()
        }

        this.category_rank = this.category_rank.genNext();

        db.put(new_category);

        return category_id;
    }

    async addLibraryCategory(library_id, timestamp) {
        const library_category = await QuestionLibrary.getStatic(library_id);
        const category_id = uuid(timestamp);

        // get controls
        const include_controls = [];
        for (let library_subcategory of [...library_category.subcategories]) {
            for (let library_control of [...library_subcategory.controls]) {
                include_controls.push(library_control.details.child_id);
            }
        }

        const new_category = {
            _id: `${this.assessment_type_id}:category:${category_id}`,
            name: library_category.details.name,
            control_number: library_category.details.control_number,
            type: "category",
            rank_: this.category_rank.toString(),
            library_id,
            include_controls
        }

        this.category_rank = this.category_rank.genNext();

        return db.put(new_category);

        /*
        let category_id = this.addCategory(new_category, timestamp++);

        for (let subcategory of library_category.subcategories) {
            let new_subcategory = {
                name: subcategory.details.name,
                control_number: subcategory.details.control_number
            }

            this.addSubcategory(category_id, new_subcategory, timestamp++, subcategory.id)
        }
        */

        return category_id;
    }

    saveCategory(category) {
        return db.put(category)
    }

    getRank(after, before) {
        let beforeRank, afterRank, newRank;

        if (after && after.details) after = after.details;
        if (before && before.details) before = before.details;

        if (!before) {
            // add to the end of the list
            newRank = this.rank.toString();
            this.rank = this.rank.genNext();
        } else {
            if (!after) {
                afterRank = LexoRank.min()
            } else {
                afterRank = LexoRank.parse(after.rank_);
            }

            beforeRank = LexoRank.parse(before.rank_);

            newRank = afterRank.between(beforeRank).toString();
        }

        return newRank;

        if (!after) {
            afterRank = LexoRank.min();
        } else {
            if (after.details) after = after.details;

            afterRank = LexoRank.parse(after.rank_);
        }

        if (before.details) before = before.details;

        beforeRank = LexoRank.parse(before.rank_);

        newRank = afterRank.between(beforeRank).toString();

        return newRank
    }

    moveCategory(category, after, before) {
        let newRank

        if (!before) {
            newRank = this.category_rank.toString();

            this.category_rank = this.category_rank.genNext();
        } else {
            newRank = this.getRank(after, before);
        }

        category.details.rank_ = newRank;

        return this.saveCategory(category.details);
    }

    addSubcategory(category_id, subcategory, timestamp, library_id) {
        if (!this.subcategory_ranks[category_id]) {
            this.subcategory_ranks[category_id] = LexoRank.middle();
        }

        let subcategory_id = uuid(timestamp);
        let new_subcategory = {
            _id: `${this.assessment_type_id}:subcategory:${category_id}:${subcategory_id}`,
            subcategory_id: subcategory_id,
            name: subcategory.name,
            control_number: subcategory.control_number,
            type: "subcategory",
            rank_: this.subcategory_ranks[category_id].toString(),
            library_id
        }

        this.subcategory_ranks[category_id] = this.subcategory_ranks[category_id].genNext()

        db.put(new_subcategory);

        return subcategory_id
    }

    async addLibrarySubcategory(category_id, library_id, timestamp) {
        let library_subcategory = await db.get(library_id);

        let new_subcategory = {
            name: library_subcategory.name,
            control_number: library_subcategory.control_number
        }

        this.addSubcategory(category_id, new_subcategory, timestamp, library_id);
    }

    saveSubcategory(subcategory) {
        return db.put(subcategory);
    }

    moveSubcategory(category, subcategory, after, before) {
        let newRank;

        if (!before) {
            newRank = this.subcategory_ranks[category.details.category_id].toString();
            this.subcategory_ranks[category.details.category_id] = this.subcategory_ranks[category.details.category_id].genNext();
        } else {
            newRank = this.getRank(after, before);
        }

        subcategory.details.rank_ = newRank;

        return this.saveSubcategory(subcategory.details);
    }

    addControl(category_id, subcategory_id, control, timestamp) {

        if (!this.control_ranks[subcategory_id]) {
            this.control_ranks[subcategory_id] = LexoRank.middle();
        }

        let control_id = uuid(timestamp);

        if (control.id) {
            control_id = control.id;
        }

        let new_control = {
            _id: `${this.assessment_type_id}:control:${category_id}:${subcategory_id}:${control_id}`,
            control_id: control_id,
            name: control.name || "",
            control_number: control.control_number,
            text: control.text || "",
            guidance: control.guidance || "",
            level: control.level || 1,
            question_type: control.question_type,
            references: control.references || [],
            topic_tags: control.topic_tags || [],
            tag_mapping: control.tag_mapping || {},
            type: "control",
            recommendation_map: control.recommendation_map || {},
            rank_: this.control_ranks[subcategory_id].toString()
        }

        this.control_ranks[subcategory_id] = this.control_ranks[subcategory_id].genNext();

        return new Promise((resolve, reject) => {
            db.put(new_control).then((result) => {
                new_control._rev = result.rev;
                resolve(new_control)
            }).catch(reject);
        });
    }

    addLibraryControl(category_id, subcategory_id, library_id, timestamp) {
        let control_id = uuid(timestamp);

        let id = `${this.assessment_type_id}:control:${category_id}:${subcategory_id}:${control_id}`;

        db.put({
            _id: id,
            control_id,
            type: "control",
            library_id: library_id,
            tag_mapping: {},
            recommendation_map: {},
            library_mappings: {}
        })

        return id;
    }

    addLibraryControlAnswerMap(control_id, answer, map, timestamp) {
        let mapping_id = uuid(timestamp);

        let id = `${this.assessment_type_id}:library_control_answer_map:${control_id}:${mapping_id}`;

        return db.put({
            _id: id,
            mapping_id,
            type: "library_control_answer_map",
            answer,
            map
        });
    }

    deleteLibraryControlAnswerMap(map) {
        map._deleted = true;
        return db.put(map);
    }

    addLibraryControlTagMap(control_id, tag_id, map, timestamp) {
        let mapping_id = uuid(timestamp);

        let id = `${this.assessment_type_id}:library_control_tag_map:${control_id}:${mapping_id}`;

        return db.put({
            _id: id,
            mapping_id,
            type: "library_control_tag_map",
            tag_id,
            map
        });
    }

    deleteLibraryControlTagMap(map) {
        map._deleted = true;
        return db.put(map);
    }

    disableLibraryControlMap(map, timestamp) {
        let disabled_map_id = uuid(timestamp)

        let id = `${this.assessment_type_id}:disabled_mapping:${disabled_map_id}`;

        return db.put({
            _id: id,
            disabled_map_id,
            type: "disabled_mapping",
            map
        });
    }

    enableLibraryControlMap(disabled_map) {
        disabled_map._deleted = true;

        return db.put(disabled_map);
    }

    moveControl(subcategory, control, after, before) {
        let newRank;
        if (!before) {
            newRank = this.control_ranks[subcategory.details.subcategory_id].toString();
            this.control_ranks[subcategory.details.subcategory_id] = this.control_ranks[subcategory.details.subcategory_id].genNext();
        } else {
            newRank = this.getRank(after, before);
        }

        control.rank_ = newRank;

        return this.saveControl(control);
    }

    getControl(category_id, subcategory_id, control_id) {
        return db.get(`${this.assessment_type_id}:control:${category_id}:${subcategory_id}:${control_id}`)
    }

    deleteCategory(category) {
        for (let subcategory of Object.values(category.subcategories)) {
            this.deleteSubcategory(subcategory);
        }
        category.details._deleted = true;
        return db.put(category.details);
    }

    deleteSubcategory(subcategory) {
        for (let control of Object.values(subcategory.controls)) {
            this.deleteControl(control)
        }
        subcategory.details._deleted = true;
        return db.put(subcategory.details);
    }

    deleteControl(control) {
        control._deleted = true;
        return db.put(control)
    }

    saveControl(control) {
        return new Promise((resolve, reject) => {
            db.put(control).then((result) => {
                control._rev = result.rev;
                resolve(control);
            }).catch(reject);
        });
    }

    saveAssessmentType() {
        return new Promise((resolve, reject) => {
            db.put(this.assessment_type_data.details).then((result) => {
                this.assessment_type_data.details._rev = result.rev;
                resolve(this.assessment_type_data.details);
            }).catch(reject);
        });
    }

    saveDetails(details) {
        return db.put(details);
    }

    addPolicy(policy) {
        policy.policy_id = uuid();
        policy._id = `${this.assessment_type_id}:policy:${policy.policy_id}`;
        policy.type = "policy";

        return db.put(policy);
    }

    deletePolicy(policy) {
        policy._deleted = true;
        return db.put(policy);
    }

    savePolicy(policy) {
        return db.put(policy);
    }

    addTag(tag) {
        if (!tag.tag_id) {
            tag.tag_id = uuid();
        }

        tag._id = `${this.assessment_type_id}:tag:${tag.tag_id}`;
        tag.type = "tag"

        return db.put(tag);
    }

    deleteTag(tag) {
        tag._deleted = true;
        return db.put(tag);
    }

    saveTag(tag) {
        return db.put(tag);
    }

    addCommentTag(tag) {
        tag.tag_id = uuid();
        tag._id = `${this.assessment_type_id}:comment_tag:${tag.tag_id}`;
        tag.type = "comment_tag"

        return db.put(tag);
    }

    deleteCommentTag(tag) {
        tag._deleted = true;
        return db.put(tag);
    }

    saveCommentTag(tag) {
        return db.put(tag);
    }

    addThreat(threat_id) {
        let threat = {
            _id: `${this.assessment_type_id}:threat:${threat_id}`,
            threat_id
        }

        return db.put(threat);
    }

    deleteThreat(threat) {
        let promises = [];
        for (let mapping in this.assessment_type_data.threat_matrix_mapping) {
            if (this.assessment_type_data.threat_matrix_mapping[mapping]._id.includes(`threatMap:${threat.threat_id}:`)) {
                let doc = this.assessment_type_data.threat_matrix_mapping[mapping];
                doc._deleted = true;
                promises.push(db.put(doc));
            }
        }

        return Promise.all(promises).then(() => {
            threat._deleted = true;
            return db.put(threat);
        })
    }

    addMatrixControlMapping(matrix_threat, matrix_control, assessment_control) {
        let map_id = uuid();

        let mapping = {
            _id: `${this.assessment_type_id}:threatMap:${matrix_threat.threat_id}:${matrix_control.control_id}:${map_id}`,
            mapped_control: assessment_control._id
        }

        return db.put(mapping);
    }

    deleteMatrixControlMapping(control_mapping) {
        control_mapping._deleted = true;
        return db.put(control_mapping);
    }

    addSection(section) {
        section.section_id = uuid();
        section._id = `${this.assessment_type_id}:report_section:${section.section_id}`;
        section.type = "report_section";
        section.rank_ = this.section_rank.toString();

        this.section_rank = this.section_rank.genNext();

        return db.put(section);
    }

    deleteSection(section) {
        section._deleted = true;
        return db.put(section);
    }

    saveSection(section) {
        return db.put(section);
    }

    moveSection(section, after, before) {
        let newRank

        if (!before) {
            newRank = this.section_rank.toString();

            this.section_rank = this.section_rank.genNext();
        } else {
            newRank = this.getRank(after, before);
        }

        section.rank_ = newRank;

        return this.saveSection(section);
    }

    addSlide(slide) {
        slide.slide_id = uuid();
        slide._id = `${this.assessment_type_id}:report_slide:${slide.slide_id}`;
        slide.type = "report_slide";

        slide.rank_ = this.slide_rank.toString();
        this.slide_rank = this.slide_rank.genNext();

        return db.put(slide);
    }

    removeSlide(slide) {
        slide._deleted = true

        return db.put(slide);
    }

    saveSlide(slide) {
        return db.put(slide);
    }


    moveSlide(slide, after, before) {
        let newRank

        if (!before) {
            newRank = this.slide_rank.toString();

            this.slide_rank = this.slide_rank.genNext();
        } else {
            newRank = this.getRank(after, before);
        }

        slide.rank_ = newRank;

        return this.saveSlide(slide);
    }

    addDashboardMapping(dashboard, dashboard_item, control_id) {
        let map_id = uuid();

        let mapping = {
            _id: `${this.assessment_type_id}:dashboardMapping:${dashboard._id}:${dashboard_item.item_id}:${map_id}`,
            type: "dashboardMapping",
            mapped_control: control_id
        }

        return db.put(mapping);
    }

    deleteDashboardMapping(control_mapping) {
        control_mapping._deleted = true;
        return db.put(control_mapping);
    }

    saveExcelTemplate(tmpl) {
        return db.put(tmpl)
    }
}